import React from 'react';
import { Typography, Grid } from '@mui/material';
import ColorPicker from './ColorPicker';

/**
 * ColorPickerSection Component
 *
 * Displays the color customization section in the theme customizer.
 * Includes brand colors and status colors.
 *
 * @param {Object} props
 * @param {Object} props.themeData - Current theme data object
 * @param {Function} props.updateThemeData - Function to update theme data
 */
function ColorPickerSection({ themeData, updateThemeData }) {
  return (
    <>
      <Typography variant="h6" gutterBottom>
        Brand Colors
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define your primary brand colors
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPicker
            label="Primary Color"
            color={themeData.primary_color || '#1976d2'}
            onChange={value => updateThemeData('primary_color', value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPicker
            label="Secondary Color"
            color={themeData.secondary_color || '#dc004e'}
            onChange={value => updateThemeData('secondary_color', value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPicker
            label="Accent Color"
            color={themeData.accent_color || '#ff9800'}
            onChange={value => updateThemeData('accent_color', value)}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Background Colors
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Set background and surface colors
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPicker
            label="Background Color"
            color={themeData.background_color || '#ffffff'}
            onChange={value => updateThemeData('background_color', value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPicker
            label="Surface Color"
            color={themeData.surface_color || '#f5f5f5'}
            onChange={value => updateThemeData('surface_color', value)}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Text Colors
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure text color hierarchy
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPicker
            label="Primary Text"
            color={themeData.text_color || '#000000'}
            onChange={value => updateThemeData('text_color', value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPicker
            label="Secondary Text"
            color={themeData.text_secondary || '#666666'}
            onChange={value => updateThemeData('text_secondary', value)}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Status Colors
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define colors for success, warning, error, and info states
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPicker
            label="Success Color"
            color={themeData.success_color || '#4caf50'}
            onChange={value => updateThemeData('success_color', value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPicker
            label="Warning Color"
            color={themeData.warning_color || '#ff9800'}
            onChange={value => updateThemeData('warning_color', value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPicker
            label="Error Color"
            color={themeData.error_color || '#f44336'}
            onChange={value => updateThemeData('error_color', value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPicker
            label="Info Color"
            color={themeData.info_color || '#2196f3'}
            onChange={value => updateThemeData('info_color', value)}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default ColorPickerSection;
